import { onBeforeUnmount } from 'vue';

/**
 * Lisse l'affichage d'une réponse reçue en streaming.
 *
 * Le proxy ne débite pas le texte régulièrement : le serveur d'inférence groupe
 * ses jetons, et un même paquet réseau porte souvent plusieurs évènements SSE.
 * Écrit tel quel à l'écran, ça donne des paquets de mots qui apparaissent d'un
 * coup — la réponse est bien en flux, mais elle saute.
 *
 * Les fragments reçus sont donc empilés dans une file, qu'une boucle
 * `requestAnimationFrame` vide caractère par caractère. Le débit est calculé à
 * chaque image pour solder la file dans un délai fixe : plus le retard est
 * grand, plus on écrit vite. Le retard reste ainsi borné à cet horizon, quelle
 * que soit la vitesse du modèle, et l'accélération est progressive au lieu de
 * se voir.
 *
 * Le calcul se fait sur le temps écoulé et non sur un nombre d'images : si le
 * rendu ralentit — un long message coûte cher à réafficher — les images
 * s'espacent, et le débit par image monte d'autant. Le lissage ne prend jamais
 * de retard sur lui-même.
 */

/** Horizon visé pour solder la file pendant la génération. */
const CATCH_UP_MS = 260;

/**
 * Horizon une fois le serveur terminé.
 *
 * C'est tout ce que le lissage ajoute au temps de réponse perçu : la fin du
 * texte s'écrit en moins d'un dixième de seconde, sans apparaître d'un bloc.
 */
const CATCH_UP_END_MS = 90;

/**
 * Délai au-delà duquel on solde la file sans attendre les images.
 *
 * `requestAnimationFrame` peut être suspendu sans que `visibilitychange` ne se
 * déclenche — fenêtre occultée, bridage du navigateur. Sans ce filet, la
 * promesse de `finish()` ne serait jamais tenue et l'écran resterait bloqué sur
 * une réponse qui n'avance plus. Les minuteries, elles, continuent de tourner.
 */
const FINISH_WATCHDOG_MS = 3000;

/**
 * Plafond du temps attribué à une image.
 *
 * Une image qui suit un long gel — onglet réactivé, tâche longue sur le fil
 * principal — ne doit pas déverser la file d'un seul coup.
 */
const MAX_FRAME_MS = 100;

export function useSmoothStream() {
    let queue = '';
    let write = null;
    let afterWrite = null;
    let frame = null;
    let lastAt = 0;
    let ended = false;
    let endPromise = null;
    let resolveEnd = null;

    // Fermé tant qu'aucune réponse n'est en cours, et de nouveau dès qu'une
    // est soldée ou abandonnée. Un fragment qui arrive après — le serveur peut
    // en avoir mis un en route au moment où l'on demande l'arrêt — est ignoré
    // au lieu de relancer l'écriture derrière le dos du lecteur.
    let closed = true;
    let watchdog = null;

    /**
     * Ouvre un lissage. Toute file en cours est abandonnée : on ne mélange pas
     * deux réponses.
     *
     * @param {Object}   handlers
     * @param {Function} handlers.write      Reçoit le texte à ajouter à l'écran
     * @param {Function} [handlers.afterWrite] Appelé après chaque écriture
     */
    function start(handlers) {
        cancelFrame();
        queue = '';
        ended = false;
        closed = false;
        settleEnd();
        write = handlers.write;
        afterWrite = handlers.afterWrite || null;

        document.addEventListener('visibilitychange', onVisibilityChange);
    }

    /** Empile un fragment reçu du serveur. */
    function push(fragment) {
        if (closed || !write || !fragment) return;

        // Onglet caché : personne ne regarde, et la boucle d'animation y est
        // suspendue. On écrit directement, sinon la file gonflerait pour se
        // déverser au retour.
        if (document.hidden) {
            write(fragment);
            return;
        }

        queue += fragment;
        schedule();
    }

    /**
     * Le serveur a fini d'émettre : solde la file au débit de fin.
     *
     * @returns {Promise<void>} tenue quand tout est à l'écran
     */
    function finish() {
        ended = true;

        if (!write || queue === '') {
            stop();
            return Promise.resolve();
        }

        if (!endPromise) {
            endPromise = new Promise((resolve) => { resolveEnd = resolve; });
            watchdog = setTimeout(flush, FINISH_WATCHDOG_MS);
        }

        schedule();

        return endPromise;
    }

    /** Écrit tout ce qui reste sans attendre. Pour un arrêt demandé ou une erreur. */
    function flush() {
        cancelFrame();

        if (queue !== '' && write) {
            const reste = queue;
            queue = '';
            write(reste);
            if (afterWrite) afterWrite();
        }

        settleEnd();
    }

    /** Referme le lissage et abandonne ce qui n'est pas encore affiché. */
    function stop() {
        cancelFrame();
        queue = '';
        closed = true;
        settleEnd();
        document.removeEventListener('visibilitychange', onVisibilityChange);
    }

    function schedule() {
        if (frame !== null) return;

        lastAt = performance.now();
        frame = requestAnimationFrame(tick);
    }

    function tick(now) {
        if (!write) {
            frame = null;
            return;
        }

        // Le temps réellement écoulé commande le débit ; le plafond protège du
        // rattrapage brutal après un gel.
        const dt = Math.min(Math.max(now - lastAt, 1), MAX_FRAME_MS);
        lastAt = now;

        const horizon = ended ? CATCH_UP_END_MS : CATCH_UP_MS;
        const taille = Math.min(queue.length, Math.ceil((queue.length * dt) / horizon));

        if (taille > 0) {
            write(queue.slice(0, taille));
            queue = queue.slice(taille);
            if (afterWrite) afterWrite();
        }

        // `frame` n'est jamais remis à zéro avant cette ligne : la garde de
        // `schedule()` tient donc pendant tout le corps de la fonction, et une
        // écriture qui pousserait un fragment ne peut pas ouvrir une seconde
        // boucle sur la même file.
        frame = queue !== '' && !closed ? requestAnimationFrame(tick) : null;

        if (queue !== '') return;

        // File vide et serveur terminé : plus rien ne viendra, on referme —
        // sans quoi l'écouteur de visibilité survivrait à la réponse.
        if (ended) stop();
    }

    function cancelFrame() {
        if (frame !== null) {
            cancelAnimationFrame(frame);
            frame = null;
        }
    }

    /** Tient la promesse de fin, s'il y en a une en attente. */
    function settleEnd() {
        if (watchdog !== null) {
            clearTimeout(watchdog);
            watchdog = null;
        }

        if (resolveEnd) resolveEnd();
        resolveEnd = null;
        endPromise = null;
    }

    function onVisibilityChange() {
        if (document.hidden) flush();
    }

    // Le composant peut disparaître pendant une génération : sans ça, la boucle
    // continuerait d'écrire dans un état qui n'est plus affiché.
    onBeforeUnmount(stop);

    return { start, push, finish, flush, stop };
}
